import React, { useState } from "react";
import { Col, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  CatalogRecord,
  ChillerFields,
  CompressorFields,
  ResourceName,
} from "./CatalogAdmin.types.tsx";
import ChillerForm from "./ChillerForm.tsx";
import CompressorModal from "./CompressorModal.tsx";
import ImportCsvModal from "./ImportCsvModal.tsx";
import RecordModal from "./RecordModal.tsx";
import ResourceList from "./ResourceList.tsx";
import { RESOURCES, isSimple } from "./resources.tsx";
import { useLookups } from "./useLookups.tsx";

import { HeaderLabel } from "../Labels.tsx";

interface CatalogAdminProps {
  canDelete: boolean;
  onSignOut: () => void;
}

interface Editing {
  record: CatalogRecord | null;
}

const CatalogAdmin: React.FC<CatalogAdminProps> = ({ canDelete, onSignOut }) => {
  const { t } = useTranslation();
  const [resource, setResource] = useState<ResourceName>("chillers");
  const [editing, setEditing] = useState<Editing | null>(null);
  const [importing, setImporting] = useState(false);
  const [version, setVersion] = useState(0);
  const [listKey, setListKey] = useState(0);
  const { lookups, reloadLookups } = useLookups();

  const refrigerants = (lookups.refrigerants ?? []).map((item) => String(item.fields.name));

  const choose = (next: ResourceName) => {
    setResource(next);
    setEditing(null);
    setImporting(false);
  };

  const saved = () => {
    setEditing(null);
    setVersion((current) => current + 1);
    reloadLookups();
  };

  const imported = (created: number) => {
    setImporting(false);
    setListKey((current) => current + 1);
    reloadLookups();
    toast.success(t("catalog.import.created", { n: created }));
  };

  if (resource === "chillers" && editing) {
    return (
      <Col className="p-0 catalog-admin">
        <div className="m-4">
          <HeaderLabel textAlign="left">
            {t(editing.record ? "catalog.chiller.editTitle" : "catalog.chiller.newTitle")}
          </HeaderLabel>
          <ChillerForm
            record={editing.record as CatalogRecord<ChillerFields> | null}
            lookups={lookups}
            onBack={() => setEditing(null)}
            onSaved={saved}
            onCompressorCreated={reloadLookups}
          />
        </div>
      </Col>
    );
  }

  return (
    <Col className="p-0 catalog-admin">
      <div className="m-4">
        <HeaderLabel textAlign="left">{t("catalog.title")}</HeaderLabel>

        <Nav
          variant="tabs"
          className="mb-3"
          activeKey={resource}
          onSelect={(key) => {
            if (key) {
              choose(key as ResourceName);
            }
          }}
        >
          {RESOURCES.map((spec) => (
            <Nav.Item key={spec.name}>
              <Nav.Link eventKey={spec.name}>{t(`catalog.resources.${spec.name}`)}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <ResourceList
          key={`${resource}-${listKey}`}
          resource={resource}
          lookups={lookups}
          version={version}
          onChanged={reloadLookups}
          onSignOut={onSignOut}
          canDelete={canDelete}
          onNew={() => setEditing({ record: null })}
          onEdit={(record) => setEditing({ record })}
          onImport={() => setImporting(true)}
        />

        {editing && isSimple(resource) && (
          <RecordModal
            key={editing.record?.id ?? "new"}
            resource={resource}
            record={editing.record}
            onHide={() => setEditing(null)}
            onSaved={saved}
          />
        )}

        {editing && resource === "compressors" && (
          <CompressorModal
            key={editing.record?.id ?? "new"}
            record={editing.record as CatalogRecord<CompressorFields> | null}
            refrigerants={refrigerants}
            onHide={() => setEditing(null)}
            onSaved={saved}
          />
        )}

        {importing && (
          <ImportCsvModal
            resource={resource}
            onHide={() => setImporting(false)}
            onImported={imported}
          />
        )}
      </div>
    </Col>
  );
};

export default CatalogAdmin;
